import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Paper, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

const columnHeaderSx = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: '#A8A49E',
  textAlign: 'right',
  width: 48,
  flexShrink: 0,
};

const countCellSx = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#1A1916',
  textAlign: 'right',
  width: 48,
  flexShrink: 0,
};

const FailureCategoriesSection = (props) => {
  const {
    isLoading,
    errorMessage,
    categoryRows,
  } = props;

  const maxCount = Math.max(1, ...categoryRows.map((categoryRow) => categoryRow.nowCount));

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          lineHeight: '14px',
          letterSpacing: '0.04em',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression='metrics.AgentReportCard.FailureCategoriesSection' />
      </Typography>
      {
        isLoading &&
        <Paper
          sx={{
            height: 160,
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
            boxShadow: 'none',
            backgroundColor: '#F5F4F2',
          }}
        />
      }
      {
        !isLoading &&
        errorMessage &&
        <Alert severity='warning'>
          {errorMessage}
        </Alert>
      }
      {
        !isLoading &&
        !errorMessage &&
        <Paper
          sx={{
            p: 3,
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
            boxShadow: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '21px',
              color: '#1A1916',
              mb: 0.75,
            }}
          >
            <Translate expression='metrics.AgentReportCard.FailureCategories.Title' />
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: '#A8A49E',
              mb: 3,
            }}
          >
            <Translate expression='metrics.AgentReportCard.FailureCategories.Subtitle' />
          </Typography>
          {
            categoryRows.length === 0 &&
            <Typography
              sx={{
                fontSize: '12px',
                color: '#A8A49E',
              }}
            >
              <Translate expression='metrics.AgentReportCard.FailureCategories.Empty' />
            </Typography>
          }
          {
            categoryRows.length > 0 &&
            <Stack spacing={1.5}>
              <Stack direction='row' alignItems='center' spacing={1.25}>
                <Typography
                  sx={{
                    ...columnHeaderSx,
                    flex: 1,
                    width: 'auto',
                    textAlign: 'left',
                  }}
                >
                  <Translate expression='metrics.AgentReportCard.FailureCategories.Category' />
                </Typography>
                <Typography sx={columnHeaderSx}>
                  <Translate expression='metrics.AgentReportCard.FailureCategories.Now' />
                </Typography>
                <Typography sx={columnHeaderSx}>
                  <Translate expression='metrics.AgentReportCard.FailureCategories.Prev' />
                </Typography>
                <Typography sx={columnHeaderSx}>
                  <Translate expression='metrics.AgentReportCard.FailureCategories.Delta' />
                </Typography>
              </Stack>
              {
                categoryRows.map((categoryRow) => {
                  const barWidth = `${(categoryRow.nowCount / maxCount) * 100}%`;
                  const deltaColor = categoryRow.delta > 0 ? '#DA7955' : categoryRow.delta < 0 ? '#21892C' : '#919191';

                  return (
                    <Stack key={categoryRow.key} direction='row' alignItems='center' spacing={1.25}>
                      <Stack direction='row' alignItems='center' spacing={1.25} sx={{flex: 1, minWidth: 0}}>
                        <Typography
                          sx={{
                            width: 170,
                            fontSize: '11px',
                            lineHeight: '14px',
                            color: '#6B6761',
                            flexShrink: 0,
                            wordBreak: 'break-word',
                          }}
                        >
                          {categoryRow.label}
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            height: 8,
                            backgroundColor: '#F0F0F0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            display: {xs: 'none', sm: 'block'},
                          }}
                        >
                          <Box
                            sx={{
                              width: barWidth,
                              minWidth: categoryRow.nowCount ? '10px' : 0,
                              height: '100%',
                              backgroundColor: '#DA7955',
                              borderRadius: '4px',
                            }}
                          />
                        </Box>
                      </Stack>
                      <Typography sx={countCellSx}>
                        {categoryRow.nowCount}
                      </Typography>
                      <Typography sx={{...countCellSx, color: '#A8A49E'}}>
                        {categoryRow.prevCount}
                      </Typography>
                      <Typography sx={{...countCellSx, color: deltaColor}}>
                        {categoryRow.delta > 0 ? `+${categoryRow.delta}` : categoryRow.delta}
                      </Typography>
                    </Stack>
                  );
                })
              }
            </Stack>
          }
        </Paper>
      }
    </Box>
  );
};

FailureCategoriesSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  categoryRows: PropTypes.array.isRequired,
};

export default React.memo(FailureCategoriesSection);
